import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet/sheet";
import { Documents } from "../documents/documents";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "@/routes";
import { useCallback, useRef, useState } from "react";
import { EXDSOpLandingDocumentList as Doc } from "@/odata/appu4dm/AppU4DMModel";
import { useTranslation } from "react-i18next";
import { DocumentViewer } from "../document-viewer/document-viewer";
import { cn } from "@/lib/utils";
import { ExpandIcon, Loader } from "lucide-react";
import { useDocuments } from "@/queries/documents/use-documents";
import { useDocument } from "@/queries/documents/use-document";
import { QueryError } from "@/components/ui/query-error/query-error";

type RouteParams = { docId: string };

enum ViewState {
  OPEN,
  CLOSING,
  CLOSED,
}

export function DocumentSheet() {
  // reference to the document viewer.
  // used to prevent overlapping in drawer events due to how radix handles dialogs
  // and to open full screen view
  const viewRef = useRef<HTMLDivElement>(null);
  // manages the sequence of closing both drawers to align animations
  // and prevent the clunky behavior of closing both drawers at the same time
  const [viewState, setViewState] = useState(ViewState.OPEN);

  const { t } = useTranslation();
  const { docId } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { documents, error, isSuccess, isPending, refetch } = useDocuments();
  const activeDoc = documents?.find((doc) => doc.Document === docId);

  // If docId is not in the (successfully fetched) list of documents
  // we will try fetch it individual
  const {
    document: fallbackDocument,
    error: fallbackError,
    isLoading: fallbackPending,
    refetch: fallbackRefetch,
  } = useDocument(isSuccess && !activeDoc ? docId : undefined);

  const collapsed = !!docId;

  const fileId = activeDoc?.FileId ?? fallbackDocument?.LocalFile_Id_Id;
  const fileName = activeDoc?.FileName ?? fallbackDocument?.FileName;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) return;
      if (docId) return setViewState(ViewState.CLOSING);
      setViewState(ViewState.CLOSED);
    },
    [navigate, docId]
  );

  const handleDocumentSelect = useCallback(
    (doc: Doc) => {
      if (!doc.Document) return;
      navigate(ROUTES.DOCUMENTS(doc.Document));
    },
    [navigate]
  );

  return (
    <>
      <Sheet
        open={viewState !== ViewState.CLOSED}
        onOpenChange={handleOpenChange}
      >
        <SheetContent
          className={cn("z-[51] shadow-2xl transition-all", {
            "w-[20%]": !!docId,
            "w-[600px]": !docId,
          })}
          onAnimationEnd={() => {
            if (viewState !== ViewState.CLOSED) return;
            navigate(ROUTES.HOME);
          }}
          onPointerDownOutside={(e) => {
            // prevent closing both sheets when closing the inner sheet
            if (!viewRef.current?.contains(e.target as Node)) return;
            e.preventDefault();
          }}
        >
          <SheetHeader>
            <SheetTitle>{t("documents.title")}</SheetTitle>
          </SheetHeader>
          <div
            className={cn(
              "px-5 pt-5 overflow-auto overflow-x-hidden h-[calc(100%-68px)] no-scrollbar",
              {
                "px-0 pt-0": collapsed,
              }
            )}
          >
            <Documents
              selected={docId}
              condense={collapsed}
              onDocumentSelect={handleDocumentSelect}
            />
          </div>
        </SheetContent>
      </Sheet>

      <Sheet
        open={viewState === ViewState.OPEN && !!docId}
        modal={false}
        onOpenChange={(open) => {
          if (open) return;

          if (document.fullscreenElement === viewRef.current)
            document.exitFullscreen();

          navigate(ROUTES.DOCUMENTS());
        }}
      >
        <SheetContent
          ref={viewRef}
          className="z-[50] right-[20%] w-[80%] h-full"
          overlay={null}
          onInteractOutside={(e) => {
            // prevent closing the inner sheet when clicking elsewhere
            e.preventDefault();
          }}
          onAnimationEnd={() => {
            if (viewState !== ViewState.CLOSING) return;
            setViewState(ViewState.CLOSED);
          }}
        >
          <SheetHeader>
            <SheetTitle className="flex items-center gap-x-4">
              <ExpandIcon
                className="h-6 w-6 cursor-pointer"
                onClick={() =>
                  document.fullscreenElement === viewRef.current
                    ? document.exitFullscreen()
                    : viewRef.current?.requestFullscreen()
                }
              />
              <span>{fileName ?? "..."}</span>
            </SheetTitle>
          </SheetHeader>
          <div className="grid h-full">
            {(error || fallbackError) && (
              <QueryError
                resource={t("documentViewer.document")}
                errors={[error, fallbackError]}
                retries={[refetch, fallbackRefetch]}
              />
            )}
            {(isPending || fallbackPending) && (
              <div className="h-full w-full flex justify-center items-center">
                <Loader className="h-24 w-24 text-gray-500 animate-spin" />
              </div>
            )}
            {fileId && (
              <DocumentViewer fileId={fileId} title={fileName ?? ""} />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
